import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    CircularProgress,
    Alert,
    IconButton,
    Tooltip,
    TextField,
    InputAdornment
} from '@mui/material';
import { ChevronRight, Search } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from '../../axiosInstance/axiosInstance';
import PermissionWrapper from '../../Components/Auth/PermissionWrapper';
import GlobalPagination from '../../Components/Common/Globalpagination';

const RetreatEnquiryList = () => {
    const [enquiries, setEnquiries] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalRecords, setTotalRecords] = useState(0);
    const [limit, setLimit] = useState(25);
    const navigate = useNavigate();

    const fetchEnquiries = useCallback(async (searchQuery = '', currentPage = 1, currentLimit = limit) => {
        try {
            setLoading(true);
            setError(null);
            const response = await axios.get('/retreat-enquiry/admin/enqueries', {
                params: {
                    page: currentPage,
                    limit: currentLimit,
                    search: searchQuery
                }
            });
            if (response && response.data) {
                const responseData = response.data;
                const dataList = responseData.data || responseData || [];
                setEnquiries(dataList);
                setTotalPages(responseData.totalPages || 1);
                setTotalRecords(responseData.totalRecords ?? responseData.total ?? ((responseData.totalPages || 1) * currentLimit));
            } else {
                setError("Failed to fetch retreat enquiries.");
            }
        } catch (err) {
            console.error("Fetch Enquiries Error:", err);
            setError(err.response?.data?.message || err.message || "Failed to load enquiries. Please try again.");
        } finally {
            setLoading(false);
        }
    }, [limit]);

    useEffect(() => {
        fetchEnquiries(searchTerm, page, limit);
    }, [fetchEnquiries, searchTerm, page, limit]);

    const handleSearchChange = (event) => {
        setSearchTerm(event.target.value);
        setPage(1);
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return '-';
        return date.toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
        });
    };

    return (
        <PermissionWrapper permission="view">
            <Box sx={{ p: { xs: 2, md: 4 }, bgcolor: '#FBFBFB', minHeight: '100vh' }}>
                {/* Header */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4, flexWrap: 'wrap', gap: 2 }}>
                    <Typography sx={{ color: '#222', fontSize: { xs: '1.5rem', md: '1.8rem' }, fontWeight: 500 }}>
                        Retreat Enquiries
                    </Typography>

                    <TextField
                        placeholder="Search enquiries..."
                        size="small"
                        value={searchTerm}
                        onChange={handleSearchChange}
                        sx={{
                            width: { xs: '100%', sm: 280 },
                            bgcolor: '#fff',
                            '& .MuiOutlinedInput-root': {
                                borderRadius: '6px',
                                height: '40px',
                                fontSize: '0.85rem',
                                '& fieldset': { borderColor: '#E0E0E0' },
                                '&:hover fieldset': { borderColor: '#BDBDBD' },
                                '&.Mui-focused fieldset': { borderColor: '#FFBD65', borderWidth: '1px' },
                            }
                        }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start" sx={{ mr: 1 }}>
                                    <Search size={18} color="#64748B" />
                                </InputAdornment>
                            ),
                        }}
                    />
                </Box>

                {error && <Alert severity="error" sx={{ mb: 3, borderRadius: '8px' }} onClose={() => setError(null)}>{error}</Alert>}

                <Paper sx={{ borderRadius: "8px", border: "1px solid #E8E8E8", overflow: "hidden" }} elevation={0}>
                    <TableContainer>
                        <Table sx={{ minWidth: 1200 }}>
                            <TableHead>
                                <TableRow sx={{ bgcolor: '#F8FAFC', borderBottom: '2px solid #EEF2F6' }}>
                                    {[
                                        { label: 'S.No', align: 'left' },
                                        { label: 'Guest Name', align: 'left' },
                                        { label: 'Retreat Title', align: 'left' },
                                        { label: 'Assigned Host', align: 'left' },
                                        { label: 'Enquiry Date', align: 'left' },
                                        { label: 'Status', align: 'left' },
                                        { label: 'Created By', align: 'left' },
                                        { label: 'Updated By', align: 'left' },
                                        { label: 'Created At', align: 'left' },
                                        { label: 'Updated At', align: 'left' },
                                        { label: 'Actions', align: 'right' },
                                    ].map((h) => (
                                        <TableCell
                                            key={h.label}
                                            align={h.align}
                                            sx={{
                                                fontWeight: 600,
                                                color: '#475569',
                                                fontSize: '0.7rem',
                                                textTransform: 'uppercase',
                                                letterSpacing: '0.6px',
                                                py: 1.5,
                                                px: 1.5,
                                                whiteSpace: 'nowrap'
                                            }}
                                        >
                                            {h.label}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {loading ? (
                                    <TableRow>
                                        <TableCell colSpan={11} align="center" sx={{ py: 8 }}>
                                            <CircularProgress size={24} sx={{ color: '#FFBD65' }} />
                                        </TableCell>
                                    </TableRow>
                                ) : enquiries.length > 0 ? (
                                    enquiries.map((item, idx) => (
                                        <TableRow key={item.id || idx} sx={{
                                            '& td': { borderBottom: '1px solid #F0F0F0' },
                                            '&:last-child td': { borderBottom: 0 }
                                        }}>
                                            <TableCell align="left" sx={{ fontSize: '0.75rem', color: '#64748B', py: 1.25, px: 1.5, whiteSpace: 'nowrap' }}>
                                                {(page - 1) * limit + idx + 1}
                                            </TableCell>
                                            <TableCell align="left" sx={{ py: 1.25, px: 1.5, maxWidth: 180 }}>
                                                <Typography sx={{ fontSize: '0.75rem', fontWeight: 500, color: '#1E293B', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={item.guestName}>
                                                    {item.guestName || 'Anonymous'}
                                                </Typography>
                                                {item.guestEmail && (
                                                    <Typography sx={{ fontSize: '0.7rem', color: '#64748B', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={item.guestEmail}>
                                                        {item.guestEmail}
                                                    </Typography>
                                                )}
                                            </TableCell>
                                            <TableCell align="left" sx={{ py: 1.25, px: 1.5, maxWidth: 220 }}>
                                                <Tooltip title={item.retreatTitle || ''}>
                                                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 500, color: '#1E293B', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                        {item.retreatTitle || 'N/A'}
                                                    </Typography>
                                                </Tooltip>
                                            </TableCell>
                                            <TableCell align="left" sx={{ py: 1.25, px: 1.5, maxWidth: 140, whiteSpace: 'nowrap' }}>
                                                <Typography sx={{ fontSize: '0.75rem', color: '#475569', textTransform: 'capitalize', overflow: 'hidden', textOverflow: 'ellipsis' }} title={item.hostName}>
                                                    {item.hostName || '-'}
                                                </Typography>
                                            </TableCell>
                                            <TableCell align="left" sx={{ py: 1.25, px: 1.5, fontSize: '0.75rem', color: '#64748B', whiteSpace: 'nowrap' }}>
                                                {formatDate(item.enquiryDate)}
                                            </TableCell>
                                            <TableCell align="left" sx={{ py: 1.25, px: 1.5, whiteSpace: 'nowrap' }}>
                                                <Box
                                                    sx={{
                                                        display: 'inline-block',
                                                        px: 1.25,
                                                        py: 0.35,
                                                        borderRadius: '4px',
                                                        bgcolor: item.bookingStatus === 'completed' ? '#E8F5E9' : (item.bookingStatus === 'rejected' || item.bookingStatus === 'cancelled' ? '#FFEBEE' : '#FFF8E1'),
                                                        color: item.bookingStatus === 'completed' ? '#2E7D32' : (item.bookingStatus === 'rejected' || item.bookingStatus === 'cancelled' ? '#C62828' : '#B78103'),
                                                        fontSize: '0.7rem',
                                                        fontWeight: 500,
                                                        textTransform: 'capitalize'
                                                    }}
                                                >
                                                    {item.bookingStatus || 'pending'}
                                                </Box>
                                            </TableCell>
                                            <TableCell align="left" sx={{ py: 1.25, px: 1.5, fontSize: '0.75rem', color: '#64748B', whiteSpace: 'nowrap' }}>
                                                {item.cuName || '-'}
                                            </TableCell>
                                            <TableCell align="left" sx={{ py: 1.25, px: 1.5, fontSize: '0.75rem', color: '#64748B', whiteSpace: 'nowrap' }}>
                                                {item.uuName || '-'}
                                            </TableCell>
                                            <TableCell align="left" sx={{ py: 1.25, px: 1.5, fontSize: '0.75rem', color: '#64748B', whiteSpace: 'nowrap' }}>
                                                {formatDate(item.createdAt || item.enquiryDate)}
                                            </TableCell>
                                            <TableCell align="left" sx={{ py: 1.25, px: 1.5, fontSize: '0.75rem', color: '#64748B', whiteSpace: 'nowrap' }}>
                                                {formatDate(item.updatedAt)}
                                            </TableCell>
                                            <TableCell align="right" sx={{ py: 1.25, px: 1.5, whiteSpace: 'nowrap' }}>
                                                <Tooltip title="View Details">
                                                    <IconButton
                                                        size="small"
                                                        sx={{ color: '#64748B', p: 0.5 }}
                                                        onClick={() => navigate(`/retreat-enquiries/${item.id}`)}
                                                    >
                                                        <ChevronRight size={18} />
                                                    </IconButton>
                                                </Tooltip>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={11} align="center" sx={{ py: 6, color: '#64748B', fontSize: '0.75rem' }}>
                                            No retreat enquiries found.
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <GlobalPagination
                        page={page}
                        pageSize={limit}
                        totalRecords={totalRecords}
                        totalPages={totalPages}
                        onPageChange={(newPage) => setPage(newPage)}
                        onPageSizeChange={(newLimit) => {
                            setLimit(newLimit);
                            setPage(1);
                        }}
                        loading={loading}
                    />
                </Paper>
            </Box>
        </PermissionWrapper>
    );
};

export default RetreatEnquiryList;
